import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BookOpen, Scale, ScrollText, Landmark, Percent, TrendingUp,
  RefreshCw, Plus, Trash2, X, Save, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const iso = (d) => d.toLocaleDateString('en-CA');
const thisMonthRange = () => {
  const n = new Date();
  return { from: iso(new Date(n.getFullYear(), n.getMonth(), 1)), to: iso(n) };
};

const SOURCE_COLORS = {
  pos_order: 'bg-emerald-500/10 text-emerald-600',
  pos_return: 'bg-red-500/10 text-red-600',
  expense: 'bg-orange-500/10 text-orange-600',
  manual: 'bg-blue-500/10 text-blue-600',
};

function useFmt(lang) {
  return useCallback((v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [lang]);
}

function LoadingState() {
  return <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
}

function DateRangeBar({ range, setRange, t }) {
  const presets = [
    { key: 'thisMonth', calc: () => thisMonthRange() },
    { key: 'lastMonth', calc: () => { const n = new Date(); return { from: iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), to: iso(new Date(n.getFullYear(), n.getMonth(), 0)) }; } },
    { key: 'thisQuarter', calc: () => { const n = new Date(); const qs = Math.floor(n.getMonth() / 3) * 3; return { from: iso(new Date(n.getFullYear(), qs, 1)), to: iso(n) }; } },
    { key: 'thisYear', calc: () => { const n = new Date(); return { from: `${n.getFullYear()}-01-01`, to: iso(n) }; } },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="ledger-date-range">
      {presets.map(p => (
        <Button key={p.key} variant="outline" size="sm" onClick={() => setRange(p.calc())} data-testid={`preset-${p.key}`}>
          {t(`ledger.${p.key}`)}
        </Button>
      ))}
      <Input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} className="w-36 h-8 text-xs" data-testid="range-from" />
      <span className="text-xs text-muted-foreground">→</span>
      <Input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} className="w-36 h-8 text-xs" data-testid="range-to" />
    </div>
  );
}

// ===== INCOME STATEMENT =====
function IncomeStatementTab({ branchId, token, t, lang }) {
  const fmt = useFmt(lang);
  const [range, setRange] = useState(thisMonthRange());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/ledger/income-statement?branch_id=${branchId}&date_from=${range.from}&date_to=${range.to}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error(t('common.failed')));
  }, [branchId, token, range, t]);

  if (!data) return <LoadingState />;
  const Row = ({ label, value, bold, negative, indent, testId }) => (
    <div className={`flex justify-between py-2 px-3 ${bold ? 'font-bold bg-muted/50 rounded-lg' : ''} ${indent ? 'ps-8' : ''}`} data-testid={testId}>
      <span className={negative ? 'text-red-500' : ''}>{label}</span>
      <span className={`tabular-nums ${negative ? 'text-red-500' : ''} ${bold && value >= 0 ? 'text-emerald-600' : ''} ${bold && value < 0 ? 'text-red-500' : ''}`}>
        {negative && value > 0 ? `(${fmt(value)})` : fmt(value)}
      </span>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="ledger-income-tab">
      <DateRangeBar range={range} setRange={setRange} t={t} />
      <Card>
        <CardContent className="p-5 text-sm space-y-1">
          <h3 className="font-semibold mb-3">{t('ledger.revenue')}</h3>
          <Row label={t('ledger.salesRevenue')} value={data.sales} indent testId="is-sales" />
          <Row label={t('ledger.salesReturns')} value={data.returns} negative indent testId="is-returns" />
          <Row label={t('ledger.discountsGiven')} value={data.discounts} negative indent testId="is-discounts" />
          <Row label={t('ledger.netRevenue')} value={data.net_revenue} bold testId="is-net-revenue" />
          <Row label={t('ledger.cogs')} value={data.cogs} negative indent testId="is-cogs" />
          <Row label={`${t('ledger.grossProfit')} (${data.gross_margin}%)`} value={data.gross_profit} bold testId="is-gross-profit" />
          <h3 className="font-semibold pt-3">{t('ledger.operatingExpenses')}</h3>
          {data.opex.map(o => (
            <Row key={o.code} label={lang === 'ar' ? o.name_ar : o.name_en} value={o.amount} negative indent testId={`is-opex-${o.code}`} />
          ))}
          <Row label={t('ledger.totalOpex')} value={data.total_opex} bold testId="is-total-opex" />
          <div className="flex justify-between py-3 px-3 mt-2 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 font-bold text-base" data-testid="is-net-income">
            <span>{t('ledger.netIncome')} ({data.net_margin}%)</span>
            <span className={`tabular-nums ${data.net_income >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{fmt(data.net_income)} {t('common.sar')}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== TRIAL BALANCE =====
function TrialBalanceTab({ branchId, token, t, lang }) {
  const fmt = useFmt(lang);
  const [range, setRange] = useState(thisMonthRange());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/ledger/trial-balance?branch_id=${branchId}&date_from=${range.from}&date_to=${range.to}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error(t('common.failed')));
  }, [branchId, token, range, t]);

  if (!data) return <LoadingState />;
  return (
    <div className="space-y-4" data-testid="ledger-trial-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <DateRangeBar range={range} setRange={setRange} t={t} />
        <Badge className={data.is_balanced ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'} data-testid="tb-balanced-badge">
          {data.is_balanced ? <CheckCircle2 className="w-3 h-3 me-1" /> : <AlertTriangle className="w-3 h-3 me-1" />}
          {data.is_balanced ? t('ledger.balanced') : t('ledger.unbalanced')}
        </Badge>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="trial-balance-table">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="p-3 text-start font-medium">{t('ledger.account')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.openingBalance')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.debit')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.credit')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.closingBalance')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.code} className="border-b border-border/50 hover:bg-muted/30" data-testid={`tb-row-${r.code}`}>
                  <td className="p-3"><span className="text-xs text-muted-foreground me-2">{r.code}</span>{lang === 'ar' ? r.name_ar : r.name_en}</td>
                  <td className="p-3 text-center tabular-nums">{fmt(r.opening)}</td>
                  <td className="p-3 text-center tabular-nums">{r.debit ? fmt(r.debit) : '—'}</td>
                  <td className="p-3 text-center tabular-nums">{r.credit ? fmt(r.credit) : '—'}</td>
                  <td className={`p-3 text-center tabular-nums font-medium ${r.closing < 0 ? 'text-red-500' : ''}`}>{fmt(r.closing)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t('ledger.noEntries')}</td></tr>}
            </tbody>
            <tfoot>
              <tr className="font-bold bg-muted/40">
                <td className="p-3">{t('common.total')}</td>
                <td className="p-3" />
                <td className="p-3 text-center tabular-nums" data-testid="tb-total-debit">{fmt(data.total_debit)}</td>
                <td className="p-3 text-center tabular-nums" data-testid="tb-total-credit">{fmt(data.total_credit)}</td>
                <td className="p-3" />
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== GENERAL LEDGER (account statement) =====
function GeneralLedgerTab({ branchId, token, t, lang }) {
  const fmt = useFmt(lang);
  const [accounts, setAccounts] = useState([]);
  const [accountCode, setAccountCode] = useState('1100');
  const [range, setRange] = useState(thisMonthRange());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/ledger/accounts?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAccounts(r.data.filter(a => !a.is_group))).catch(() => {});
  }, [branchId, token]);

  useEffect(() => {
    if (!branchId || !accountCode) return;
    axios.get(`${API}/api/ledger/general-ledger?branch_id=${branchId}&account_code=${accountCode}&date_from=${range.from}&date_to=${range.to}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error(t('common.failed')));
  }, [branchId, token, accountCode, range, t]);

  return (
    <div className="space-y-4" data-testid="ledger-gl-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={accountCode} onValueChange={setAccountCode}>
          <SelectTrigger className="w-64" data-testid="gl-account-select"><SelectValue placeholder={t('ledger.selectAccount')} /></SelectTrigger>
          <SelectContent>
            {accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} — {lang === 'ar' ? a.name_ar : a.name_en}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeBar range={range} setRange={setRange} t={t} />
      </div>
      {!data ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <div className="flex justify-between p-4 border-b border-border text-sm">
              <span className="text-muted-foreground">{t('ledger.openingBalance')}: <b className="text-foreground tabular-nums">{fmt(data.opening_balance)}</b></span>
              <span className="text-muted-foreground">{t('ledger.closingBalance')}: <b className="text-foreground tabular-nums" data-testid="gl-closing-balance">{fmt(data.closing_balance)}</b></span>
            </div>
            <table className="w-full text-sm" data-testid="gl-table">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="p-3 text-start font-medium">{t('ledger.date')}</th>
                  <th className="p-3 text-start font-medium">{t('ledger.entryNumber')}</th>
                  <th className="p-3 text-start font-medium">{t('ledger.description')}</th>
                  <th className="p-3 text-center font-medium">{t('ledger.debit')}</th>
                  <th className="p-3 text-center font-medium">{t('ledger.credit')}</th>
                  <th className="p-3 text-center font-medium">{t('ledger.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="p-3 whitespace-nowrap">{r.date}</td>
                    <td className="p-3 text-xs text-muted-foreground">{r.entry_number}</td>
                    <td className="p-3 max-w-[240px] truncate">{r.description}</td>
                    <td className="p-3 text-center tabular-nums">{r.debit ? fmt(r.debit) : '—'}</td>
                    <td className="p-3 text-center tabular-nums">{r.credit ? fmt(r.credit) : '—'}</td>
                    <td className="p-3 text-center tabular-nums font-medium">{fmt(r.balance)}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('ledger.noEntries')}</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== JOURNAL =====
function JournalTab({ branchId, token, t, lang }) {
  const fmt = useFmt(lang);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [sourceType, setSourceType] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(() => {
    if (!branchId) return;
    const st = sourceType === 'all' ? '' : sourceType;
    axios.get(`${API}/api/ledger/journal?branch_id=${branchId}&page=${page}&limit=20&source_type=${st}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error(t('common.failed')));
  }, [branchId, token, page, sourceType, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/ledger/accounts?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAccounts(r.data.filter(a => !a.is_group))).catch(() => {});
  }, [branchId, token]);

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/api/ledger/journal/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('ledger.entryDeleted'));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
  };

  if (!data) return <LoadingState />;
  return (
    <div className="space-y-4" data-testid="ledger-journal-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Select value={sourceType} onValueChange={v => { setSourceType(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="journal-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="pos_order">{t('ledger.srcPosOrder')}</SelectItem>
            <SelectItem value="pos_return">{t('ledger.srcPosReturn')}</SelectItem>
            <SelectItem value="expense">{t('ledger.srcExpense')}</SelectItem>
            <SelectItem value="manual">{t('ledger.srcManual')}</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="je-add-btn">
          <Plus className="w-4 h-4 me-1.5" />{t('ledger.addEntry')}
        </Button>
      </div>

      <div className="space-y-2">
        {data.items.map(je => (
          <Card key={je.id} className="overflow-hidden" data-testid={`je-row-${je.entry_number}`}>
            <button className="w-full text-start" onClick={() => setExpanded(expanded === je.id ? null : je.id)} data-testid={`je-toggle-${je.entry_number}`}>
              <div className="flex items-center gap-3 p-3">
                <Badge className={`${SOURCE_COLORS[je.source_type] || SOURCE_COLORS.manual} text-[10px] flex-shrink-0`}>
                  {t(`ledger.src${je.source_type === 'pos_order' ? 'PosOrder' : je.source_type === 'pos_return' ? 'PosReturn' : je.source_type === 'expense' ? 'Expense' : 'Manual'}`)}
                </Badge>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{je.entry_number}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{je.date}</span>
                <span className="text-sm flex-1 min-w-0 truncate">{je.description}</span>
                <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{fmt(je.total_debit)} {t('common.sar')}</span>
                {expanded === je.id ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
              </div>
            </button>
            {expanded === je.id && (
              <div className="border-t border-border bg-muted/20 p-3" data-testid={`je-lines-${je.entry_number}`}>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="p-1.5 text-start font-medium">{t('ledger.account')}</th>
                      <th className="p-1.5 text-center font-medium">{t('ledger.debit')}</th>
                      <th className="p-1.5 text-center font-medium">{t('ledger.credit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {je.lines.map((l, i) => {
                      const acct = accounts.find(a => a.code === l.account_code);
                      return (
                        <tr key={i} className="border-t border-border/40">
                          <td className="p-1.5">{l.account_code} — {acct ? (lang === 'ar' ? acct.name_ar : acct.name_en) : ''}</td>
                          <td className="p-1.5 text-center tabular-nums">{l.debit ? fmt(l.debit) : '—'}</td>
                          <td className="p-1.5 text-center tabular-nums">{l.credit ? fmt(l.credit) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {je.source_type === 'manual' && (
                  <div className="flex justify-end mt-2">
                    <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => deleteEntry(je.id)} data-testid={`je-delete-${je.entry_number}`}>
                      <Trash2 className="w-3.5 h-3.5 me-1" />{t('common.delete')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
        {data.items.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">{t('ledger.noEntries')}</CardContent></Card>}
      </div>

      {data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="journal-prev">‹</Button>
          <span className="text-sm py-1.5 text-muted-foreground">{page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} data-testid="journal-next">›</Button>
        </div>
      )}

      {showModal && <ManualJEModal branchId={branchId} token={token} t={t} lang={lang} accounts={accounts}
        onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} fmt={fmt} />}
    </div>
  );
}

function ManualJEModal({ branchId, token, t, lang, accounts, onClose, onSaved, fmt }) {
  const [date, setDate] = useState(iso(new Date()));
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([
    { account_code: '', debit: '', credit: '' },
    { account_code: '', debit: '', credit: '' },
  ]);
  const [saving, setSaving] = useState(false);

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const setLine = (i, key, val) => setLines(ls => ls.map((l, j) => j === i ? { ...l, [key]: val } : l));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/ledger/journal`, {
        branch_id: branchId, date, description,
        lines: lines.map(l => ({ account_code: l.account_code, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('ledger.entrySaved'));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="je-modal">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{t('ledger.addEntry')}</h2>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="je-modal-close"><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="je-date" />
            <Input placeholder={t('ledger.description')} value={description} onChange={e => setDescription(e.target.value)} data-testid="je-description" />
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`je-line-${i}`}>
                <Select value={l.account_code} onValueChange={v => setLine(i, 'account_code', v)}>
                  <SelectTrigger className="flex-1" data-testid={`je-line-account-${i}`}><SelectValue placeholder={t('ledger.selectAccount')} /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} — {lang === 'ar' ? a.name_ar : a.name_en}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" className="w-28" placeholder={t('ledger.debit')} value={l.debit}
                  onChange={e => setLine(i, 'debit', e.target.value)} data-testid={`je-line-debit-${i}`} />
                <Input type="number" className="w-28" placeholder={t('ledger.credit')} value={l.credit}
                  onChange={e => setLine(i, 'credit', e.target.value)} data-testid={`je-line-credit-${i}`} />
                <Button variant="ghost" size="icon" className="text-red-500 flex-shrink-0" disabled={lines.length <= 2}
                  onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} data-testid={`je-line-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setLines(ls => [...ls, { account_code: '', debit: '', credit: '' }])} data-testid="je-add-line">
            <Plus className="w-3.5 h-3.5 me-1" />{t('ledger.addLine')}
          </Button>
          <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 text-sm">
            <span>{t('ledger.totalDebit')}: <b className="tabular-nums">{fmt(totalDebit)}</b></span>
            <span>{t('ledger.totalCredit')}: <b className="tabular-nums">{fmt(totalCredit)}</b></span>
            <Badge className={balanced ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'} data-testid="je-balanced-badge">
              {balanced ? t('ledger.balanced') : t('ledger.unbalanced')}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={!balanced || saving} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="je-save-btn">
              <Save className="w-4 h-4 me-2" />{t('ledger.saveEntry')}
            </Button>
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== BALANCE SHEET =====
function BalanceSheetTab({ branchId, token, t, lang }) {
  const fmt = useFmt(lang);
  const [asOf, setAsOf] = useState(iso(new Date()));
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/ledger/balance-sheet?branch_id=${branchId}&as_of=${asOf}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error(t('common.failed')));
  }, [branchId, token, asOf, t]);

  if (!data) return <LoadingState />;
  const Section = ({ title, rows, total, totalLabel, extra, testId }) => (
    <Card data-testid={testId}>
      <CardContent className="p-5 space-y-1 text-sm">
        <h3 className="font-semibold mb-2">{title}</h3>
        {rows.map(r => (
          <div key={r.code} className="flex justify-between py-1.5 ps-3">
            <span className="text-muted-foreground">{lang === 'ar' ? r.name_ar : r.name_en}</span>
            <span className={`tabular-nums ${r.balance < 0 ? 'text-red-500' : ''}`}>{fmt(r.balance)}</span>
          </div>
        ))}
        {extra}
        <div className="flex justify-between py-2 px-3 mt-1 rounded-lg bg-muted/50 font-bold">
          <span>{totalLabel}</span><span className="tabular-nums">{fmt(total)}</span>
        </div>
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-4" data-testid="ledger-balance-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('ledger.asOf')}</span>
          <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="w-40 h-8" data-testid="bs-as-of" />
        </div>
        <Badge className={data.is_balanced ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'} data-testid="bs-balanced-badge">
          {data.is_balanced ? <CheckCircle2 className="w-3 h-3 me-1" /> : <AlertTriangle className="w-3 h-3 me-1" />}
          {data.is_balanced ? t('ledger.balanced') : t('ledger.unbalanced')}
        </Badge>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title={t('ledger.assets')} rows={data.assets} total={data.total_assets} totalLabel={t('ledger.totalAssets')} testId="bs-assets" />
        <Section title={t('ledger.liabilities')} rows={data.liabilities} total={data.total_liabilities} totalLabel={t('ledger.totalLiabilities')} testId="bs-liabilities" />
        <Section title={t('ledger.equity')} rows={data.equity} total={data.total_equity} totalLabel={t('ledger.totalEquity')} testId="bs-equity"
          extra={
            <div className="flex justify-between py-1.5 ps-3">
              <span className="text-muted-foreground">{t('ledger.currentEarnings')}</span>
              <span className={`tabular-nums ${data.current_earnings < 0 ? 'text-red-500' : ''}`}>{fmt(data.current_earnings)}</span>
            </div>
          } />
      </div>
    </div>
  );
}

// ===== VAT RETURN =====
function VatReturnTab({ branchId, token, t, lang }) {
  const fmt = useFmt(lang);
  const now = new Date();
  const [quarter, setQuarter] = useState(String(Math.floor(now.getMonth() / 3) + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/ledger/vat-return?branch_id=${branchId}&quarter=${quarter}&year=${year}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => toast.error(t('common.failed')));
  }, [branchId, token, quarter, year, t]);

  if (!data) return <LoadingState />;
  const cards = [
    { label: t('ledger.vatOnSales'), value: data.vat_on_sales, color: 'text-emerald-600' },
    { label: t('ledger.vatOnReturns'), value: data.vat_on_returns, color: 'text-red-500' },
    { label: t('ledger.outputVat'), value: data.output_vat, color: 'text-amber-600' },
    { label: t('ledger.inputVat'), value: data.input_vat, color: 'text-blue-600' },
  ];
  return (
    <div className="space-y-4" data-testid="ledger-vat-tab">
      <div className="flex items-center gap-2">
        <Select value={quarter} onValueChange={setQuarter}>
          <SelectTrigger className="w-32" data-testid="vat-quarter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 2, 3, 4].map(q => <SelectItem key={q} value={String(q)}>{t('ledger.quarter')} {q}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28" data-testid="vat-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[0, 1, 2].map(o => { const y = now.getFullYear() - o; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <Card key={i} data-testid={`vat-card-${i}`}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-1">{c.label}</p>
              <p className={`text-lg font-bold tabular-nums ${c.color}`}>{fmt(c.value)}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-amber-500/30">
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{t('ledger.netVatDue')}</p>
            {data.net_vat_due < 0 && <p className="text-xs text-emerald-600 mt-0.5">{t('ledger.refundable')}</p>}
          </div>
          <p className={`text-2xl font-bold tabular-nums ${data.net_vat_due >= 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="vat-net-due">
            {fmt(Math.abs(data.net_vat_due))} {t('common.sar')}
          </p>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Card><CardContent className="p-4 flex justify-between"><span className="text-muted-foreground">{t('ledger.netSales')}</span><b className="tabular-nums">{fmt(data.net_sales)}</b></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between"><span className="text-muted-foreground">{t('ledger.salesReturns')}</span><b className="tabular-nums text-red-500">{fmt(data.returns)}</b></CardContent></Card>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="vat-monthly-table">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="p-3 text-start font-medium">{t('accounting.month')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.netSales')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.outputVat')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.inputVat')}</th>
                <th className="p-3 text-center font-medium">{t('ledger.netVatDue')}</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly.map(m => (
                <tr key={m.month} className="border-b border-border/50">
                  <td className="p-3">{m.month}/{data.year}</td>
                  <td className="p-3 text-center tabular-nums">{fmt(m.net_sales)}</td>
                  <td className="p-3 text-center tabular-nums">{fmt(m.output_vat)}</td>
                  <td className="p-3 text-center tabular-nums">{fmt(m.input_vat)}</td>
                  <td className="p-3 text-center tabular-nums font-medium">{fmt(m.net_vat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== PAGE =====
export default function LedgerPage() {
  const { token, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const branchId = activeBranch?.id;
  const [tab, setTab] = useState('income');
  const [rebuilding, setRebuilding] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const rebuild = async () => {
    if (!window.confirm(t('ledger.rebuildConfirm'))) return;
    setRebuilding(true);
    try {
      const { data } = await axios.post(`${API}/api/ledger/rebuild?branch_id=${branchId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${t('ledger.rebuildDone')} (${data.posted})`);
      setRefreshKey(k => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    setRebuilding(false);
  };

  const tabs = [
    { key: 'income', icon: TrendingUp, label: t('ledger.incomeStatement') },
    { key: 'trial', icon: Scale, label: t('ledger.trialBalance') },
    { key: 'gl', icon: BookOpen, label: t('ledger.generalLedger') },
    { key: 'journal', icon: ScrollText, label: t('ledger.journal') },
    { key: 'balance', icon: Landmark, label: t('ledger.balanceSheet') },
    { key: 'vat', icon: Percent, label: t('ledger.vatReturn') },
  ];

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-6" data-testid="ledger-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('ledger.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('ledger.subtitle')} — {activeBranch?.name || ''}</p>
          </div>
          <Button variant="outline" onClick={rebuild} disabled={rebuilding || !branchId} data-testid="ledger-rebuild-btn">
            <RefreshCw className={`w-4 h-4 me-2 ${rebuilding ? 'animate-spin' : ''}`} />{t('ledger.rebuild')}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full grid grid-cols-3 sm:grid-cols-6 h-auto p-1 bg-muted/50" data-testid="ledger-tabs">
            {tabs.map(tb => (
              <TabsTrigger key={tb.key} value={tb.key} className="text-xs sm:text-sm py-2" data-testid={`tab-${tb.key}`}>
                <tb.icon className="w-4 h-4 me-1.5 hidden sm:block" />{tb.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="income"><IncomeStatementTab key={`i${refreshKey}`} branchId={branchId} token={token} t={t} lang={lang} /></TabsContent>
          <TabsContent value="trial"><TrialBalanceTab key={`t${refreshKey}`} branchId={branchId} token={token} t={t} lang={lang} /></TabsContent>
          <TabsContent value="gl"><GeneralLedgerTab key={`g${refreshKey}`} branchId={branchId} token={token} t={t} lang={lang} /></TabsContent>
          <TabsContent value="journal"><JournalTab key={`j${refreshKey}`} branchId={branchId} token={token} t={t} lang={lang} /></TabsContent>
          <TabsContent value="balance"><BalanceSheetTab key={`b${refreshKey}`} branchId={branchId} token={token} t={t} lang={lang} /></TabsContent>
          <TabsContent value="vat"><VatReturnTab key={`v${refreshKey}`} branchId={branchId} token={token} t={t} lang={lang} /></TabsContent>
        </Tabs>
      </div>
    </ModuleLayout>
  );
}
